import { openDB } from "idb";

const DB_NAME = `rowan-cache`;
const STORE_NAME = `publication-store`;

export const getDb = async () => {
  return openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" });
      }
    },
  });
};

export const getCacheItem = async (key: string) => {
  const db = await getDb();
  const item = await db.get(STORE_NAME, key);
  if (item) {
    item.lastAccessed = Date.now();
    await db.put(STORE_NAME, item);
  }
  return item;
};

export const setCacheItem = async (
  key: string,
  contents: string,
  publicationSlug: string,
  lastAccessed?: number,
) => {
  const db = await getDb();
  const record: Record<string, string | number> = {
    key,
    contents,
    lastAccessed: Date.now(),
    publicationSlug,
  };
  if (typeof lastAccessed === "number") record.lastAccessed = lastAccessed;
  return db.put(STORE_NAME, record);
};

export const deleteCachedPublication = async (publicationSlug: string) => {
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const all = await tx.store.getAll();
  const prefix = `${publicationSlug}/`;

  for (const item of all) {
    if (item.key.startsWith(prefix)) {
      await tx.store.delete(item.key);
    }
  }

  // delete the meta key
  const metaKey = `meta:${publicationSlug}`;
  await tx.store.delete(metaKey);
};

export const evictOldestPublication = async () => {
  const db = await getDb();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const all = await tx.store.getAll();

  const metas = all
    .filter((item) => item.key.startsWith("meta:"))
    .sort((a, b) => (a.lastUsed ?? 0) - (b.lastUsed ?? 0));

  const oldest = metas[0];
  if (!oldest) return;

  const publicationSlug = oldest.key.slice("meta:".length);

  deleteCachedPublication(publicationSlug);
};

const isQuotaExceededError = (err: unknown): boolean => {
  return (
    (err instanceof DOMException && err.name === "QuotaExceededError") ||
    (typeof err === "object" &&
      err !== null &&
      "name" in err &&
      err.name === "QuotaExceededError")
  );
};

export const trySetCacheItemWithEviction = async (
  key: string,
  contents: string,
  publicationSlug: string,
) => {
  try {
    if (publicationSlug) {
      await setCacheItem(
        `meta:${publicationSlug}`,
        "",
        publicationSlug,
        Date.now(),
      );
    }
    await setCacheItem(key, contents, publicationSlug, Date.now());
  } catch (err: unknown) {
    if (isQuotaExceededError(err)) {
      console.warn("Quota exceeded. Evicting and retrying...");
      await evictOldestPublication();
      try {
        await setCacheItem(key, contents, publicationSlug, Date.now());
      } catch (retryErr) {
        console.error("Retry after eviction failed", retryErr);
        throw retryErr;
      }
    } else {
      throw err;
    }
  }
};
